import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { getToken } from "./token";
import CryptoJS from "crypto-js";
import { useAuthStore } from "@/stores/auth.store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;
const API_SECRET = import.meta.env.VITE_API_SECRET;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// ---- API Error handling subscription ----
export interface ApiErrorInfo {
  status: number;
  message: string;
  url?: string;
  statusText?: string;
}

type ApiErrorListener = (info: ApiErrorInfo | null) => void;
const apiErrorListeners = new Set<ApiErrorListener>();

export const ERROR_STATUS_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

export function subscribeApiError(listener: ApiErrorListener) {
  apiErrorListeners.add(listener);
  return () => {
    apiErrorListeners.delete(listener);
  };
}

export function clearApiError() {
  apiErrorListeners.forEach((l) => l(null));
}

function emitApiError(info: ApiErrorInfo) {
  apiErrorListeners.forEach((l) => l(info));
}

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers ?? {};

    const token = getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (API_KEY && API_SECRET) {
      const timestamp = Date.now().toString();
      const signature = CryptoJS.HmacSHA256(timestamp, API_SECRET)
        .toString(CryptoJS.enc.Hex)
        .toLowerCase();

      config.headers["x-api-key"] = API_KEY;
      config.headers["x-timestamp"] = timestamp;
      config.headers["x-signature"] = signature;
    }

    // console.log("[Request]", {
    //   method: config.method,
    //   url: `${config.baseURL}${config.url}`,
    //   data: config.data,
    //   headers: config.headers,
    // });

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const statusText = error.response?.statusText || error.message;

    console.error("[Axios Error]", status, statusText, error.config?.url);

    // Handle client errors (4xx) and server errors (5xx)
    if (status) {
      const errorInfo: ApiErrorInfo = {
        status,
        message: statusText,
        url: error.config?.url,
        statusText,
      };

      // Emit error for all error statuses
      if (status >= 400) {
        emitApiError(errorInfo);
      }

      // Specific error handling based on status code
      switch (status) {
        case ERROR_STATUS_CODES.NOT_FOUND:
          console.warn("[404] Resource not found:", error.config?.url);
          break;
        case ERROR_STATUS_CODES.UNAUTHORIZED:
          console.warn("[401] token invalid or expired");
          if (window.location.pathname !== "/auth") {
            useAuthStore.getState().logout();
            window.location.href = "/auth";
          }
          break;
        case ERROR_STATUS_CODES.FORBIDDEN:
          console.warn("[403] Forbidden - Access denied");
          break;
        case ERROR_STATUS_CODES.BAD_REQUEST:
          console.warn("[400] Bad request - Check request parameters");
          break;
        case ERROR_STATUS_CODES.SERVER_ERROR:
        case ERROR_STATUS_CODES.SERVICE_UNAVAILABLE:
          console.error("[5xx] Server error:", statusText);
          break;
      }
    }

    return Promise.reject(error);
  },
);

export type { AxiosRequestConfig };
